// /users/:id — one person's profile, as seen by another signed-in member.
//
// Built 2026-09-09, the first page in the app that shows a person rather than an
// event or a group. It exists because avatars now exist: before today there was
// nothing to put on it.
//
// ⚠ THIS VIEW MAKES NO PERMISSION DECISIONS, and must not start. The server's
// `projectProfile()` is the single place that decides what a viewer gets, and it
// sends a withheld field as '' rather than omitting it. So the rule here is
// always "render it if it is non-empty" — never "render it if the viewer is an
// admin", which would be a second copy of a rule that has to agree with the
// first one forever.
//
// Deliberately NOT here: any global activity or results count. `event_results`
// rows are group-scoped, so a site-wide total would tell a stranger how active
// someone is and roughly in how many groups. `groupsInCommon` is safe by
// construction — the server only ever names groups the viewer is already in.

import { ApiClientError, blockUser, fetchUserProfile, unblockUser } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import type { UserProfile } from '@msc/shared';
import { reportButton } from './reportControl.js';

export const profileView: View = async (outlet, ctx) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  const userId = Number(ctx.params['userId'] ?? 0);
  if (!Number.isInteger(userId) || userId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'That is not a valid profile link.'));
    return;
  }

  let profile: UserProfile;
  try {
    profile = await fetchUserProfile(userId);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(
      el('h1', {}, 'Profile'),
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.status === 404
          ? 'There is no such member. The account may have been closed.'
          : 'Could not load that profile.',
      ),
      el('p', {}, [el('a', { href: '/groups' }, 'Back to your groups')]),
    );
    return;
  }

  // Re-rendered in place after a block or unblock, so the button and the notice
  // agree with each other without a reload.
  const paint = (p: UserProfile) => main.replaceChildren(...render(p, paint));
  paint(profile);
};

function render(p: UserProfile, repaint: (p: UserProfile) => void): HTMLElement[] {
  const name = [p.firstName, p.lastName].filter((s) => s !== '').join(' ') || 'This member';

  return [

    el('div', { class: 'profile-head' }, [
      p.avatarUrl
        ? // The FULL-SIZE image here, not the header thumb: this is the one place
          // the original resolution earns its keep, and it is why both sizes are
          // stored instead of only the small one.
          el('img', { class: 'profile-photo', src: p.avatarUrl, alt: `${name}'s photo` })
        : el('div', { class: 'profile-photo profile-photo-empty', 'aria-hidden': 'true' }, initials(p)),
      el('div', { class: 'profile-ident' }, [
        el('h1', {}, name),
        ...(p.memberSince
          ? [el('p', { class: 'muted small' }, `Member since ${formatMonth(p.memberSince)}`)]
          : []),
        // Non-empty means the server decided this viewer may see it. No check here.
        ...(p.phone
          ? [
              el('p', {}, [
                el('a', { href: `tel:${p.phone}` }, formatPhone(p.phone)),
              ]),
            ]
          : []),
        ...(p.isSelf
          ? [
              el('p', { class: 'muted small' }, [
                'This is you. ',
                el('a', { href: '/account' }, 'Edit your details'),
                '.',
              ]),
            ]
          : []),
      ]),
    ]),

    // Guideline 1.2 wanted a way to report an abusive USER, and until
    // 2026-09-09 only their CONTENT could be reported -- useless against
    // someone who posts nothing, or deletes as they go. reportButton returns
    // null for your own profile, so there is no self-report to offer.
    //
    // Block sits beside it because they are the two things you do about the
    // same person: report tells the operator, block changes what you see.
    ...(p.isSelf ? [] : [el('p', { class: 'profile-actions' }, actions(p, repaint))]),

    // A block is silent by design -- the other person is never told -- so the
    // only place it is visible is here. Without this line, someone who blocked
    // a member months ago has no way to work out why that member's posts stopped
    // appearing.
    ...(p.blockedByMe
      ? [
          el(
            'p',
            { class: 'muted small' },
            'You blocked this member. You will not see each other’s posts, ' +
              'chat messages or photos. Event announcements are unaffected.',
          ),
        ]
      : []),

    el('section', { class: 'card' }, [
      el('h2', {}, p.isSelf ? 'Your groups' : 'Groups you share'),
      ...(p.groupsInCommon.length === 0
        ? [
            el(
              'p',
              { class: 'muted small' },
              p.isSelf
                ? 'You are not in any groups yet.'
                : `You and ${p.firstName || 'this member'} are not in any groups together.`,
            ),
          ]
        : [
            el(
              'ul',
              { class: 'plain-list' },
              p.groupsInCommon.map((g) =>
                el('li', {}, [el('a', { href: `/groups/${g.groupId}` }, g.name)]),
              ),
            ),
          ]),
    ]),
  ];
}

/**
 * Report and Block, the two things you do about a person.
 *
 * The block button re-fetches nothing: the server returns the new state and the
 * only thing on this page that depends on it is the notice, so the profile is
 * repainted from a locally amended copy. Cheaper than a round trip and it
 * cannot show a state the server did not just confirm.
 */
function actions(p: UserProfile, repaint: (p: UserProfile) => void): HTMLElement[] {
  const out: HTMLElement[] = [];
  const report = reportButton('user', p.userId, p.isSelf);
  if (report) out.push(report);

  const btn = el(
    'button',
    { type: 'button', class: 'btn btn-link' },
    p.blockedByMe ? 'Unblock' : 'Block',
  ) as HTMLButtonElement;

  btn.addEventListener('click', async () => {
    btn.setAttribute('disabled', 'disabled');
    try {
      const res = p.blockedByMe ? await unblockUser(p.userId) : await blockUser(p.userId);
      repaint({ ...p, blockedByMe: res.blocked });
    } catch {
      // Left deliberately quiet apart from re-enabling: a failed block is not
      // a state to explain in a paragraph on a profile page, and the button
      // still says what it will do next.
      btn.removeAttribute('disabled');
    }
  });
  out.push(btn);
  return out;
}

/** Matches the account page and the feed, so one person reads the same everywhere. */
function initials(p: UserProfile): string {
  const both = `${p.firstName.charAt(0)}${p.lastName.charAt(0)}`.toUpperCase();
  return both.trim() || '?';
}

/**
 * "September 2026" from a `YYYY-MM-DD`.
 *
 * Parsed by hand rather than `new Date(iso)`: a bare date string is treated as
 * UTC midnight, so a viewer west of UTC renders the previous MONTH at the start
 * of one. Same class of bug as the all-day event rule.
 */
function formatMonth(iso: string): string {
  const [y, m] = iso.split('-');
  const year = Number(y);
  const month = Number(m);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) return iso;
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${names[month - 1]} ${year}`;
}

/** Light formatting for a stored 10 or 11 digit number. Anything else is shown as-is. */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
